/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'

import {
  Switch,
  FormControl,
  TextInput,
  Dropdown,
  InputGroup,
  TextareaInput,
  DatePicker,
  Loader,
  AutoSuggest,
} from 'components'
import { StockKeepingUnitService } from 'features/StockKeepingUnit'
import { IStockKeepingUnitSingleProps } from 'features/StockKeepingUnit/model'
import {
  findSelected,
  getImageFileName,
  trimFirstSpace,
  statusFilter,
} from 'helpers/common'
import type { ILoaderOption, ProductForForm } from 'types'

import defaultImage from 'assets/svg/default-image-rectangle.svg'
import ReactTooltip from 'react-tooltip'

const InfoSection: React.FC = () => {
  const { productId } = useParams<{ id: string; productId?: string }>()
  const { control, errors, setValue, trigger, clearErrors, getValues } =
    useFormContext<ProductForForm>()
  const [, setNameChecking] = useState<boolean>(false)
  const [dateOfPurchaseChecking, setDateOfPurchaseChecking] =
    useState<boolean>(false)
  const [skuData, setSkuData] = useState<IStockKeepingUnitSingleProps>()
  const imageProduct = control.defaultValuesRef.current.imageProduct
  const { t } = useTranslation(['product', 'common'])
  const base = 'f-product-form'

  const isEdit = Boolean(productId)

  const fetchSkuName = async (
    searchQuery: string,
    loadedOptions: unknown[],
    options: { page: number },
  ) => {
    const payload = {
      ...(searchQuery && {
        'name[like]': `%${searchQuery.trim()}%`,
      }),
      _limit: 10,
      _page: options.page,
      isActive: true,
    }

    const { body: _data, totals } = await StockKeepingUnitService.findAll(
      payload,
    )
    const currentTotal = loadedOptions.length + _data.length

    const optionsWithTooltip = {
      options: _data.map((option) => ({
        value: option?.name,
        label:
          option?.name.length > 40 ? (
            <>
              <ReactTooltip
                role='tooltip'
                className='cb-select-tooltip'
                type='dark'
                place='right'
                effect='solid'
                id={String(option?.id)}
              />
              <div
                data-for={option?.id}
                data-tip={option?.name}
                data-event='mouseover focus'
                data-event-off='mouseout blur'
                aria-label='option'
                className='selectOptionWithTooltip'
              >
                {option?.name}
              </div>
            </>
          ) : (
            option.name
          ),
        key: option.id,
        ...option,
      })),
      hasMore: currentTotal < totals,
      additional: {
        page: payload._page + 1,
      },
    }
    return optionsWithTooltip
  }
  const setDetail = (data: IStockKeepingUnitSingleProps) => {
    setValue('skuId', data.id, { shouldDirty: true })
    setValue('name', data.name, { shouldDirty: true })
    setValue('brand', data.brand, { shouldDirty: true })
    setValue('detail', data.detail, { shouldDirty: true })
    setValue('currency', data.saleCurrency, { shouldDirty: true })
    setValue('price', data.salePrice, { shouldDirty: true })
  }

  const handleSearchName = (item: any): void => {
    setSkuData(item)
    setDetail(item)
    clearErrors('name')
  }

  const getSelectedSearch = (value: string): ILoaderOption => {
    return {
      key: value,
      label: value,
      value,
    }
  }

  return (
    <div className={`${base}__section--with-border`}>
      <div className={`${base}__title-layout`}>
        <div>
          <div className={`${base}__title`}>{t('product:product.title')}</div>
          <div className={`${base}__logo-text`}>
            {t('product:productImage.title')}
          </div>
          <img
            className={`${base}__product-logo`}
            src={
              skuData && !isEmpty(skuData.photos)
                ? getImageFileName(skuData.photos[0] as string)
                : !isEmpty(imageProduct)
                ? getImageFileName(imageProduct as string)
                : defaultImage
            }
            alt='product-logo'
          />
        </div>
      </div>
      <div className={`${base}__description-layout`}>
        <div className={`${base}__fields`}>
          <FormControl>
            <Controller
              as={TextInput}
              type='hidden'
              name='currentState'
              control={control}
            />
            <Controller
              as={TextInput}
              type='hidden'
              name='skuId'
              control={control}
            />
          </FormControl>

          <div className={`${base}__grid`}>
            <FormControl label={t('product:product.name.label')} required>
              <Controller
                type='text'
                name='name'
                control={control}
                render={(field) => {
                  const defaultSelected = skuData
                    ? getSelectedSearch(skuData.name)
                    : undefined
                  const getSelected = skuData
                    ? getSelectedSearch(skuData.name)
                    : getSelectedSearch(field.value)
                  const selected = isEdit ? getSelected : defaultSelected

                  const isError = Boolean(errors.name?.message)

                  return (
                    <AutoSuggest
                      ref={field.ref}
                      placeholder={t('product:product.name.placeholder')}
                      loadOptions={fetchSkuName}
                      message={isError ? 'product:name.required' : ''}
                      isError={isError}
                      onChange={handleSearchName}
                      onBlur={async () => {
                        setNameChecking(true)
                        await trigger('name')
                        setNameChecking(false)
                      }}
                      selected={selected}
                      disabled={isEdit}
                      isSearchable
                    />
                  )
                }}
              />
            </FormControl>
            <FormControl label={t('product:product.statusAvailable.label')}>
              <Controller
                name='isActive'
                control={control}
                render={(fields) => (
                  <Switch
                    className={`${base}__switch`}
                    label={statusFilter(fields.value)}
                    checked={fields.value}
                    onChange={(e) => fields.onChange(e.target.checked)}
                  />
                )}
              />
            </FormControl>
          </div>

          <div className={`${base}__grid`}>
            <FormControl label={t('product:product.brand.label')}>
              <Controller
                as={TextInput}
                type='text'
                name='brand'
                control={control}
                placeholder={t('product:product.brand.placeholder')}
                isError={Boolean(errors.brand?.message)}
                message={errors.brand?.message}
                disabled
              />
            </FormControl>
            <FormControl label={t('product:product.price.label')}>
              <InputGroup
                variants='ternary'
                error={errors.currency?.message || errors.price?.message}
              >
                <Controller
                  type='text'
                  name='currency'
                  control={control}
                  render={(field) => (
                    <Dropdown
                      className={`${base}__prefix`}
                      group='right'
                      options={[{ key: 'THB', label: 'THB' }]}
                      selected={findSelected(
                        [{ key: 'THB', label: 'THB' }],
                        field.value,
                      )}
                      isError={
                        Boolean(errors.currency?.message) ||
                        Boolean(errors.price?.message)
                      }
                      disabled
                      {...field}
                    />
                  )}
                />
                <Controller
                  type='text'
                  name='price'
                  control={control}
                  render={(field) => (
                    <TextInput
                      editBranchWDropdown
                      group='left'
                      placeholder={t('product:product.price.placeholder')}
                      isError={
                        Boolean(errors.currency?.message) ||
                        Boolean(errors.price?.message)
                      }
                      disabled
                      {...field}
                    />
                  )}
                />
              </InputGroup>
            </FormControl>
          </div>

          <div className={`${base}__grid`}>
            <FormControl label={t('product:product.serialNumber.label')}>
              <Controller
                name='serialNumber'
                control={control}
                render={(field) => (
                  <TextInput
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(trimFirstSpace(e))
                    }}
                    onBlur={async () => {
                      const currentValue = getValues('serialNumber')
                      setValue('serialNumber', currentValue?.trim())
                    }}
                    placeholder={t('product:product.serialNumber.placeholder')}
                    isError={Boolean(errors.serialNumber?.message)}
                    message={errors.serialNumber?.message}
                    maxLength={50}
                    type='text'
                    ref={field.ref}
                  />
                )}
              />
            </FormControl>
            <FormControl
              label={t('product:product.dateOfPurchase.label')}
              required
            >
              <Controller
                name='dateOfPurchase'
                control={control}
                render={(fields) => (
                  <DatePicker
                    {...fields}
                    onBlur={async () => {
                      if (!isEdit) {
                        setDateOfPurchaseChecking(true)
                        await trigger('dateOfPurchase')
                        setDateOfPurchaseChecking(false)
                      }
                    }}
                    withIcon={dateOfPurchaseChecking && <Loader />}
                  />
                )}
                isError={Boolean(errors.dateOfPurchase?.message)}
                message={errors.dateOfPurchase?.message}
              />
            </FormControl>
          </div>

          <FormControl label={t('product:product.detail.label')}>
            <Controller
              name='detail'
              control={control}
              render={(fields) => (
                <TextareaInput
                  {...fields}
                  disabled
                  placeholder={t('product:product.detail.placeholder')}
                  isError={Boolean(errors.detail?.message)}
                  message={errors.detail?.message}
                  onChange={(e) => {
                    fields.onChange(trimFirstSpace(e))
                  }}
                />
              )}
            />
          </FormControl>
        </div>
      </div>
    </div>
  )
}

export default InfoSection
